import {
  createComponent,
  createContext,
  createMemo,
  createSignal,
  getOwner,
  onCleanup,
  runWithOwner,
  untrack,
  useContext,
  type ParentProps,
} from "solid-js"
import { fetchTools, getCachedTools, preloadToolsData, type ToolCatalogItem, type ToolsResponse } from "@/hooks/use-tools"
import { readUserSessionToken } from "@/storage/user-session"
import {
  deleteCapability,
  getCapability,
  listCapabilities,
  saveCapability,
  type CapabilityDetail,
  type CapabilityDraft,
  type CapabilitySummary,
} from "@/transport/capabilities"
import { deleteTool } from "@/transport/tools"
import { createQuery, type Query } from "@/utils/query"

/**
 * Account-scoped capability library: the runtime tool catalog, library
 * summaries, and definitions. Data survives navigation, renders from cache on
 * return, revalidates when a surface starts observing it, and refreshes from
 * control-plane events instead of per-page streams.
 */

// Summaries are account data; the cache is disposable and scoped to the signed-in user.
let cachedSummaries: { scope: string; value: CapabilitySummary[] } | undefined
let inflightSummaries: { scope: string; promise: Promise<CapabilitySummary[]> } | undefined

function cachedSummaryList() {
  return cachedSummaries?.scope === readUserSessionToken() ? cachedSummaries.value : undefined
}

function fetchSummaries(options?: { force?: boolean; signal?: AbortSignal }) {
  const scope = readUserSessionToken()
  if (!options?.force && inflightSummaries?.scope === scope) return inflightSummaries.promise
  const promise = listCapabilities(options?.signal).then((value) => {
    if (readUserSessionToken() === scope) cachedSummaries = { scope, value }
    return value
  })
  if (!options?.force) {
    inflightSummaries = { scope, promise }
    void promise.catch(() => undefined).finally(() => {
      if (inflightSummaries?.promise === promise) inflightSummaries = undefined
    })
  }
  return promise
}

/** Warms the Capabilities page before navigation. */
export function preloadCapabilityLibrary(options?: { signal?: AbortSignal }) {
  return Promise.all([
    preloadToolsData(options),
    cachedSummaryList() ? undefined : fetchSummaries({ signal: options?.signal }),
  ])
}

export type CapabilityLibraryChange = { kind: "capability"; id?: string } | { kind: "tools" }

type DefinitionEntry = { query: Query<CapabilityDetail>; hold: () => void }

// Coalesces event bursts (one save publishes several updates) into one request.
function coalesced(run: () => void) {
  let timer: ReturnType<typeof setTimeout> | undefined
  onCleanup(() => clearTimeout(timer))
  return () => {
    clearTimeout(timer)
    timer = setTimeout(run, 75)
  }
}

function createCapabilityLibrary() {
  const owner = getOwner()
  const [observers, setObservers] = createSignal(0)
  const observed = () => observers() > 0

  const catalog = createQuery(
    observed,
    (_, { signal, refresh }): Promise<ToolsResponse> => (refresh ? fetchTools({ force: true, signal }) : fetchTools()),
    { initialValue: getCachedTools() },
  )
  const summaries = createQuery(
    observed,
    (_, { signal, refresh }) => (refresh ? fetchSummaries({ force: true, signal }) : fetchSummaries()),
    { initialValue: cachedSummaryList() },
  )

  const definitions = new Map<string, DefinitionEntry>()
  const definition = (id: string) => {
    const existing = definitions.get(id)
    if (existing) return existing
    const created = runWithOwner(owner, () => {
      const [holders, setHolders] = createSignal(0)
      const query = createQuery(() => holders() > 0 && id, (id, { signal }) => getCapability(id, signal))
      return {
        query,
        hold: () => {
          setHolders((count) => count + 1)
          onCleanup(() => setHolders((count) => count - 1))
        },
      }
    })!
    definitions.set(id, created)
    return created
  }

  const refreshCatalog = coalesced(() => void catalog.refresh())
  const refreshSummaries = coalesced(() => void summaries.refresh())
  const refreshDefinitions = new Map<string, () => void>()
  const refreshDefinition = (id: string) => {
    let refresh = refreshDefinitions.get(id)
    if (!refresh) {
      refresh = runWithOwner(owner, () => coalesced(() => void definitions.get(id)?.query.refresh()))!
      refreshDefinitions.set(id, refresh)
    }
    refresh()
  }

  // A mutation response replaces local state; a request already in flight
  // predates it, so restart that request rather than let it overwrite.
  const settle = <T,>(query: Query<T>, value: T) => {
    const stale = query.loading || query.error !== undefined
    query.mutate(value)
    if (stale) void query.refresh()
  }

  return {
    catalog,
    summaries,
    /** Titles by ID for everything in the shared capability namespace: skills, tools, slash commands, and machine tools. */
    usedNames: createMemo(() => new Map<string, string>([
      ...(catalog()?.tools ?? []).map((tool) => [tool.id, tool.manifest.name] as const),
      ...(summaries() ?? []).map((item) => [item.id, item.title] as const),
    ])),
    /** Registers the calling component as showing library data. */
    observe() {
      if (untrack(observers) === 0) {
        if (untrack(catalog) === undefined) catalog.mutate(getCachedTools())
        if (untrack(summaries) === undefined) summaries.mutate(cachedSummaryList())
      }
      setObservers((count) => count + 1)
      onCleanup(() => setObservers((count) => count - 1))
    },
    definition: (id: string) => definition(id).query,
    /** The current definition, from cache when it is loaded. */
    load: async (id: string) => untrack(definition(id).query) ?? getCapability(id),
    /** Keeps a definition loaded and current while the calling component is mounted. */
    holdDefinition: (id: string) => definition(id).hold(),
    apply(change: CapabilityLibraryChange) {
      refreshCatalog()
      if (change.kind === "tools") return
      refreshSummaries()
      for (const id of change.id ? [change.id] : definitions.keys()) {
        if (definitions.has(id)) refreshDefinition(id)
      }
    },
    async save(draft: CapabilityDraft) {
      const saved = await saveCapability(draft)
      settle(summaries, [...(untrack(summaries) ?? []).filter((item) => item.id !== saved.id), saved])
      settle(definition(saved.id).query, saved)
      return saved
    },
    async remove(item: CapabilityDetail) {
      await deleteCapability(item.id, item.revision)
      // Removing a customized command restores its built-in definition under the same ID.
      await Promise.all([summaries.refresh(), catalog.refresh(), definitions.get(item.id)?.query.refresh()])
    },
    async removeTool(tool: ToolCatalogItem) {
      await deleteTool(tool.qualified_id)
      await catalog.refresh()
    },
  }
}

export type CapabilityLibrary = ReturnType<typeof createCapabilityLibrary>

const libraries = new Set<CapabilityLibrary>()

/** Routes control-plane capability and tool events to every mounted library. */
export function applyCapabilityLibraryChange(change: CapabilityLibraryChange) {
  for (const library of libraries) library.apply(change)
}

const CapabilityLibraryContext = createContext<CapabilityLibrary>()

export function CapabilityLibraryProvider(props: ParentProps) {
  const library = createCapabilityLibrary()
  libraries.add(library)
  onCleanup(() => libraries.delete(library))
  return createComponent(CapabilityLibraryContext.Provider, {
    value: library,
    get children() {
      return props.children
    },
  })
}

export function useCapabilityLibrary() {
  const library = useContext(CapabilityLibraryContext)
  if (!library) throw new Error("useCapabilityLibrary must be used within CapabilityLibraryProvider")
  return library
}
